import { useMutation, useQueryClient } from '@tanstack/react-query';

import { IWebhookCredentials, IWebhookCredentialsUpsert } from '../types';
import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';
import { MutationConfig } from '@/lib/react-query';

type ImperativeRequestFn = ReturnType<typeof useImperativeRequestWrapper>[1];

interface IUpsertWebhookCredentialsArgs {
  configId: string;
  values: IWebhookCredentialsUpsert;
}

const upsertWebhookCredentials =
  ({
    partnerId,
    makeRequest,
  }: {
    partnerId: string;
    makeRequest: ImperativeRequestFn;
  }) =>
  async ({
    configId,
    values,
  }: IUpsertWebhookCredentialsArgs): Promise<IWebhookCredentials> => {
    const { data, error } = await makeRequest({
      path: `/partners/${partnerId}/webhooks/configs/${configId}/credentials`,
      method: 'post',
      body: values,
    });
    if (error || !data)
      throw new Error(error?.message ?? 'Upsert credentials failed');

    return data;
  };

type UseUpsertWebhookCredentialsOptions = {
  partnerId: string;
  config?: MutationConfig<ReturnType<typeof upsertWebhookCredentials>>;
};

export const useUpsertWebhookCredentials = ({
  partnerId,
  config,
}: UseUpsertWebhookCredentialsOptions) => {
  const [, makeRequest] = useImperativeRequestWrapper('lpVillageV3');
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    mutationFn: upsertWebhookCredentials({ partnerId, makeRequest }),
    onSuccess: (_) => {
      queryClient.invalidateQueries({
        queryKey: ['webhook-configs', partnerId],
      });
    },
  });
};
